import { action, computed, makeObservable, override } from 'mobx';
import { createContext, useContext } from 'react';
import { redirectToRoute } from 'routes/redirectToRoute';
import { QuoteRouteV2Id } from 'routes/routesConfigV2';
import QuoteFlowDelegate from 'store/flows/quote/quoteFlowDelegate';
import IMedicalCategoryPage, {
  MedicalCategoryPageDomainParams,
} from 'store/modules/subscriber/health-questionnaire/iMedicalCategoryPage';
import { ImqBrokerHealthQuestionnaireDomainParams } from '../health-questionnaire-entry/pageStore';

export type ImqBrokerMedicalCategoryDomainParams = ImqBrokerHealthQuestionnaireDomainParams &
  MedicalCategoryPageDomainParams;

export default class ImqBrokerMedicalCategoryPageStore extends IMedicalCategoryPage {
  protected readonly _domainParams: ImqBrokerMedicalCategoryDomainParams;
  public readonly flowDelegate: QuoteFlowDelegate;

  public override get domainParams(): ImqBrokerMedicalCategoryDomainParams {
    return this._domainParams;
  }

  public get isInitialLoading(): boolean {
    return this.flowDelegate.isFlowDataLoading || this.isPageContentLoading;
  }

  public returnBack(): void {
    const { groupId, periodId, cartId, subscriberId, entityId } = this.domainParams;
    redirectToRoute(QuoteRouteV2Id.IMQ_ADD_EMPLOYEES_HEALTH_QUESTIONNAIRE, {
      groupId,
      periodId,
      cartId,
      subscriberId,
      entityId,
    });
  }

  protected navigateToCategory(categoryName: string): void {
    const { groupId, periodId, cartId, subscriberId, entityId } = this.domainParams;

    redirectToRoute(QuoteRouteV2Id.IMQ_ADD_EMPLOYEES_HEALTH_QUESTIONNAIRE_MEDICAL_CATEGORY, {
      groupId,
      periodId,
      cartId,
      subscriberId,
      entityId,
      category: encodeURIComponent(categoryName),
    });
  }

  public override async getInitialData(): Promise<void> {
    this.flowDelegate.setIsInitialDataLoading(true);
    await this.flowDelegate.getFlowInitialData();
    await super.getInitialData();
    this.flowDelegate.setIsInitialDataLoading(false);
  }

  constructor(domainParams: ImqBrokerMedicalCategoryDomainParams) {
    super(domainParams);
    this._domainParams = domainParams;
    this.flowDelegate = new QuoteFlowDelegate({
      domainParams: {
        groupId: domainParams.groupId,
        periodId: domainParams.periodId,
        cartId: domainParams.cartId,
      },
      nextPageId: QuoteRouteV2Id.IMQ_ADD_EMPLOYEES_HEALTH_QUESTIONNAIRE,
    });

    makeObservable<ImqBrokerMedicalCategoryPageStore, 'navigateToCategory'>(this, {
      isInitialLoading: computed,
      domainParams: override,
      returnBack: action.bound,
      navigateToCategory: action.bound,
      getInitialData: override,
    });
  }
}

const ImqBrokerMedicalCategoryPageStoreContext = createContext(
  new ImqBrokerMedicalCategoryPageStore({} as ImqBrokerMedicalCategoryDomainParams),
);

export const useImqBrokerMedicalCategoryPageStore = (): ImqBrokerMedicalCategoryPageStore => {
  return useContext(ImqBrokerMedicalCategoryPageStoreContext);
};

export { ImqBrokerMedicalCategoryPageStoreContext };
